import React, { FC, useState } from "react";
import { useNavigate } from "react-router";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { useAppDispatch } from "src/hooks/redux";
import {
  changeCloneStatusAsync,
  deleteCloneAsync,
} from "src/redux/myClones/actions";
import { Link } from "src/components/Link";
import { ViewersLabel } from "src/components/Card/ViewersLabel";
import { AddToFavorite } from "src/components/Card/AddToFavoriteButton/AddToFavorite";
import { CardPrice } from "src/components/Card/CardPrice";
import { Property } from "src/components/Properties/Property";
import { combineSlugId } from "src/utils/combineSlugId";
import { getItemPath } from "src/utils/getItemPath";
import { getImageUrl } from "src/utils/getImageUrl";
import { PATHNAMES } from "src/constants/routes";
import { SCREEN_BREAKPOINTS } from "src/constants/screenBreakpoints";
import savingsIcon from "src/assets/icons/savings.svg";
import { CloneStatus, IClone } from "src/@types/clones";
import { ActionButtons } from "./ActionButtons";
import { propertiesTrns } from "./trns";
import { Show } from "../PrivateRoute/Show";
import { ONLY_FOR } from "../PrivateRoute/types";
import { TextEditorView } from "../TextEditor/TextEditorView";

const INVESTMENT_ICON = {
  title: "Investment",
  url: savingsIcon,
};

interface Props {
  clone: IClone;
  isEditable?: boolean;
}

export const CloneCard: FC<Props> = ({
  clone: {
    id,
    status,
    company_name,
    clone_cost,
    describe_product,
    main_photo,
    slug,
    views_count,
    investment_for_start,
    host,
    isFavorite,
    saved_count,
  },
  isEditable = false,
}) => {
  const { t } = useTranslation();
  const isXLScreenBreakpoint = useMediaQuery({
    maxWidth: SCREEN_BREAKPOINTS.XL,
  });

  const navigate = useNavigate();

  const [isFavoriteClone, setIsFavoriteClone] = useState(isFavorite);
  const [isLoadingPause, setIsLoadingPause] = useState(false);

  const dispatch = useAppDispatch();

  const combinedSlug = combineSlugId(slug, id);
  const cloneItemPath = getItemPath(PATHNAMES.CLONE_ITEM, {
    slug: combinedSlug,
  });
  const cloneEditPath = getItemPath(PATHNAMES.EDIT_CLONE, {
    slug: combinedSlug,
  });

  const isPaused = status === CloneStatus.PAUSE;

  const onEdit = () => navigate(cloneEditPath);
  const onDelete = () => dispatch(deleteCloneAsync(id));
  const onPause = () => {
    setIsLoadingPause(true);
    dispatch(
      changeCloneStatusAsync({
        id,
        status: CloneStatus.PAUSE,
        onFinally: () => setIsLoadingPause(false),
      })
    );
  };

  const onRestore = () => {
    setIsLoadingPause(true);
    dispatch(
      changeCloneStatusAsync({
        id,
        status: CloneStatus.ACTIVE,
        onFinally: () => setIsLoadingPause(false),
      })
    );
  };

  return (
    <div
      className={cn(
        "flex flex-col md:flex-row border border-black-base/20 rounded-20 md:h-52 w-full overflow-hidden",
        {
          "bg-purple-light": isPaused,
        }
      )}
    >
      <div className="flex self-center h-40 xs:h-60 sm:h-52 md:max-w-76 w-full">
        <Link
          className="flex rounded-20 overflow-hidden w-full"
          href={cloneItemPath}
        >
          <img
            className="w-full object-cover object-center rounded-20 hover:scale-110"
            src={getImageUrl(main_photo)}
            alt={company_name}
          />
        </Link>
      </div>

      <div className="flex flex-1 flex-col gap-3 xl:gap-4 p-3 xl:p-5">
        <div className="flex flex-col-reverse gl:flex-row justify-between">
          <Link
            href={cloneItemPath}
            className="gl:max-w-44 xl:max-w-75 2xl:max-w-105"
          >
            <h4
              className="break-all line-clamp-1 hover:text-purple-base"
              title={company_name}
            >
              {company_name}
            </h4>
          </Link>

          <div className="flex items-center justify-between gl:justify-start gap-5">
            <ViewersLabel viewsCount={views_count} />

            {!isEditable && (
              <Show onlyFor={ONLY_FOR.AGENT}>
                <AddToFavorite
                  id={id}
                  saved_count={saved_count}
                  isFavorite={isFavoriteClone}
                  setIsFavorite={setIsFavoriteClone}
                />
              </Show>
            )}
          </div>
        </div>

        <TextEditorView
          viewType="text"
          className="line-clamp-2 sm:line-clamp-1 gl:line-clamp-2 break-all text-sm"
        >
          {describe_product}
        </TextEditorView>

        <hr className="h-px bg-black-base opacity-20 border-0" />

        {isEditable ? (
          <ActionButtons
            isLoadingPause={isLoadingPause}
            isPaused={isPaused}
            onDelete={onDelete}
            onPause={onPause}
            onRestore={onRestore}
            onEdit={onEdit}
          />
        ) : (
          <div className="flex flex-col xl:flex-row justify-between xl:items-center gap-1">
            {isXLScreenBreakpoint ? (
              <>
                <Property
                  className="!p-0 gap-2.5"
                  label={t(propertiesTrns.investment)}
                  value={t(propertiesTrns.investmentValue, {
                    count: investment_for_start,
                  })}
                  icon={INVESTMENT_ICON}
                />
                <div className="flex justify-between">
                  <CardPrice price={clone_cost} />
                  <img
                    src={getImageUrl(host?.location?.country?.icon)}
                    alt={host?.location?.country?.name}
                    className="max-w-12 max-h-8"
                    title={host?.location?.country?.slug}
                  />
                </div>
              </>
            ) : (
              <>
                <CardPrice price={clone_cost} />
                <Property
                  className="!p-0 gap-2.5"
                  label={t(propertiesTrns.investment)}
                  value={t(propertiesTrns.investmentValue, {
                    count: investment_for_start,
                  })}
                  icon={INVESTMENT_ICON}
                />
                <img
                  src={getImageUrl(host.location.country.icon)}
                  alt={host.location.country.name}
                  className="max-w-12 max-h-8"
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
